import { types, Instance, flow, toGenerator } from 'mobx-state-tree';

import UserModificationModel from '@/stores/models/UserModificationModel';
import { setUserProperties } from '@/services/api/userApi';
import { config } from '@/config';

const UserModel = types
  .model('UserModel', {
    id: types.number,
    firstName: types.maybeNull(types.string),
    lastName: types.maybeNull(types.string),
    email: types.maybeNull(types.string),
    is_internal: types.maybeNull(types.boolean),
    modifications: types.optional(types.array(UserModificationModel), []),
    // User-properties bag mirrored from the backend (users.properties JSON column). Currently
    // only STREAM_TV_LOGS is read; kept as a generic bag to match the phone app's pattern.
    properties: types.optional(types.frozen<Record<string, unknown>>(), {}),
  })
  .views((self) => ({
    get displayName(): string {
      const parts = [self.firstName, self.lastName].filter(Boolean);
      return parts.length > 0 ? parts.join(' ') : '';
    },
    modificationDisplayNamesHiding(templateTypes: string[]): string[] {
      // Display names of the active modifications that block at least one of the
      // given template types — i.e. the actual reason something is hidden. Anything
      // hidden for non-modification reasons won't surface a name here. Deduped so the
      // pregnancy trimesters all collapse to a single "Pregnancy".
      return [
        ...new Set(
          self.modifications
            .filter(({ modification }) =>
              templateTypes.some(
                (templateType) =>
                  modification.getAvailableFitnessLevelsForTemplateType(
                    templateType,
                  ).length === 0,
              ),
            )
            .map(({ modification }) => modification.displayName)
            .filter(Boolean),
        ),
      ];
    },
    get canSeeDebugScreen(): boolean {
      const stageHasDebugAlwaysEnabled = ['dev', 'master', 'staging'].includes(
        config.env,
      );

      return self.is_internal === true || __DEV__ || stageHasDebugAlwaysEnabled;
    },
    get streamTvLogs(): boolean {
      // The user's own opt-in (the raw STREAM_TV_LOGS property). Drives the settings toggle.
      return self.properties?.STREAM_TV_LOGS === true;
    },
    get shouldStreamTvLogs(): boolean {
      // Whether we should actually stream Logs for this user: their opt-in OR the per-stage
      // default (non-prod stages stream for everyone). Drives the Sentry Logs gate.
      return (
        self.properties?.STREAM_TV_LOGS === true ||
        config.enableLogsStreamByDefault
      );
    },
    isWorkoutTypeAvailableForUser(workoutTemplateType: string): boolean {
      return self.modifications.every(
        ({ modification }) =>
          modification.getAvailableFitnessLevelsForTemplateType(
            workoutTemplateType,
          ).length > 0,
      );
    },
  }))
  .actions((self) => ({
    // Persist the STREAM_TV_LOGS opt-in and re-hydrate `properties` from the response.
    // Updating `properties` re-fires the StoreProvider autorun, which flips the Sentry
    // Logs gate immediately (see stores/StoreProvider.tsx / services/logging/logStreaming.ts).
    setStreamTvLogs: flow(function* (enabled: boolean) {
      const { data } = yield* toGenerator(
        setUserProperties(self.id, { STREAM_TV_LOGS: enabled }),
      );
      if (data?.user) {
        self.properties = data.user.properties ?? {};
      }
    }),
  }));

export type IUserModel = Instance<typeof UserModel>;

export default UserModel;
